import { createFileRoute, Link, Outlet, useParams } from "@tanstack/react-router"
import { ArrowLeft, Building2, Settings, UserPlus } from "lucide-react"
import type { ReactNode } from "react"
import { useActiveOrg } from "@/core/active-event"
import { useUserOrgs } from "@/core/orgs"
import { requireSession } from "@/lib/auth-guard"
import { cn } from "@/core/utils"

// Org-level settings — lives OUTSIDE the event messaging shell (it's org-scoped,
// there's no active event). Its own slim chrome: Attend mark + org name + a way
// back to the event picker, then a left sub-nav into the sections. `useActiveOrg`
// resolves the URL orgSlug → orgId and pins the BA session to that org.
export const Route = createFileRoute("/$orgSlug/settings")({
  beforeLoad: ({ location }) => requireSession(location.href),
  component: OrgSettingsLayout,
})

type NavItem = { to: string; label: string; icon: ReactNode }

const NAV: NavItem[] = [
  { to: "/$orgSlug/settings", label: "Overview", icon: <Settings /> },
  { to: "/$orgSlug/settings/people", label: "Members & invites", icon: <UserPlus /> },
]

function OrgSettingsLayout() {
  const params = useParams({ from: "/$orgSlug/settings" })
  useActiveOrg()
  const org = useUserOrgs((s) => s.orgs.find((o) => o.slug === params.orgSlug))
  const orgName = org?.name ?? humanizeSlug(params.orgSlug)

  return (
    <div className="app-canvas flex h-dvh flex-col">
      {/* Slim header */}
      <header className="flex h-12 shrink-0 items-center gap-3 border-b border-plum-200/50 px-4 dark:border-white/10">
        <img
          src="https://files.attend.vip/logo/logo_extra-small.svg"
          alt="Attend"
          className="h-2.5 w-auto dark:brightness-0 dark:invert"
        />
        <span className="flex min-w-0 items-center gap-2">
          {org?.logo ? (
            <img src={org.logo} alt="" className="h-5 w-5 rounded object-cover" />
          ) : (
            <span className="grid h-5 w-5 place-items-center rounded bg-plum-100 text-plum-700/70 dark:bg-white/10 dark:text-plum-200/55">
              <Building2 className="h-3 w-3" />
            </span>
          )}
          <span className="truncate text-[14px] font-semibold text-plum-900 dark:text-plum-50">
            {orgName}
          </span>
          <span className="rounded-md bg-plum-100 px-1.5 py-0.5 text-[10.5px] font-semibold text-plum-700/60 dark:bg-white/10 dark:text-plum-200/45">
            Organization
          </span>
        </span>
        <Link
          to="/"
          className="ml-auto inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[12.5px] font-medium text-plum-700/70 transition hover:bg-plum-100/60 dark:text-plum-200/55 dark:hover:bg-white/5"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          All events
        </Link>
      </header>

      <div className="relative flex min-h-0 flex-1">
        <nav className="flex w-[58px] shrink-0 flex-col gap-0.5 overflow-y-auto border-r border-plum-200/50 p-2 @[560px]:w-[200px] @[560px]:p-3 dark:border-white/10">
          <p className="mb-1 hidden px-2 text-[11px] font-semibold uppercase tracking-wide text-plum-700/40 @[560px]:block dark:text-plum-200/30">
            Settings
          </p>
          {NAV.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              params={params}
              activeOptions={{ exact: item.to === "/$orgSlug/settings" }}
              className={cn(
                "group flex items-center gap-2.5 rounded-lg px-2 py-2 text-[13px] font-medium text-plum-800/80 transition hover:bg-plum-100/60 @[560px]:px-3 dark:text-plum-200/70 dark:hover:bg-white/5",
                "[&.active]:bg-[var(--accent)]/12 [&.active]:text-[var(--accent)] [&.active]:font-semibold",
              )}
              activeProps={{ className: "active" }}
              title={item.label}
            >
              <span className="grid h-5 w-5 shrink-0 place-items-center [&>svg]:h-[18px] [&>svg]:w-[18px]">
                {item.icon}
              </span>
              <span className="hidden truncate @[560px]:block">{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className="relative min-w-0 flex-1">
          <Outlet />
        </div>
      </div>
    </div>
  )
}

function humanizeSlug(slug: string | undefined): string {
  if (!slug) return ""
  return slug
    .split("-")
    .map((w) => (w ? w[0]!.toUpperCase() + w.slice(1) : w))
    .join(" ")
}
